<template>
  <div>
    <!-- alertType: check -->
    <alert-check v-show="showCheckModal" ref="checkModal">
      <template #content>
        <pre>{{ propsMsg }}</pre>
        <article class="btnArea">
          <button class="btn blue" ref="checkRef" @click="closeModal">
            확인
          </button>
        </article>
      </template>
      <template #button>
        <button class="btn closeBtn" ref="checkCloseBtn" @click="closeModal">
          <span class="hidden">닫기</span>
        </button>
      </template>
    </alert-check>

    <!-- alertType: warning -->
    <alert-warning v-show="showWarningModal" ref="warningModal">
      <template #content>
        <pre>{{ propsMsg }}</pre>
        <article class="btnArea">
          <button class="btn blue" ref="warningRef" @click="closeModal">
            확인
          </button>
        </article>
      </template>
      <template #button>
        <button class="btn closeBtn" ref="warningCloseBtn" @click="closeModal">
          <span class="hidden">닫기</span>
        </button>
      </template>
    </alert-warning>

    <!-- alertType: confirm -->
    <alert-check v-show="showConfirmModal" ref="confirmModal">
      <template #content>
        <pre>{{ propsMsg }}</pre>
        <article class="btnArea">
          <button
            style="padding: 0 30px"
            class="btn border"
            ref="confirmCancelBtn"
            @click="cancelModal"
          >
            {{ propsConfirmCancelMessage }}
          </button>
          <button
            style="padding: 0 30px"
            class="btn blue"
            ref="confirmRef"
            @click="confirmCheckModal"
          >
            {{ propsConfirmOkMessage }}
          </button>
        </article>
      </template>
      <template #button>
        <button class="btn closeBtn" ref="confirmCloseBtn" @click="closeModal">
          <span class="hidden">닫기</span>
        </button>
      </template>
    </alert-check>
  </div>
</template>

<script>
import AlertCheck from '@/components/AlertCheck.vue';
import AlertWarning from '@/components/AlertWarning.vue';

export default {
  props: [
    'msg',
    'alertType',
    'url',
    'confirmCancelMessage',
    'confirmOkMessage',
  ],

  data() {
    return {
      propsMsg: '',
      propsConfirmCancelMessage: '',
      propsConfirmOkMessage: '',
      showCheckModal: false,
      showWarningModal: false,
      showConfirmModal: false,
      inertElements: [],
      focusableElements: [],
      firstFocusableEl: null,
      lastFocusableEl: null,
    };
  },

  components: {
    AlertCheck,
    AlertWarning,
  },

  watch: {
    alertType() {
      this.matchType();
    },
  },

  mounted() {
    this.matchType();
  },

  methods: {
    matchType() {
      if (this.alertType === 'warning') {
        this.showWarningModal = true;
        this.propsMsg = this.msg;
        this.$nextTick(() => {
          this.setupFocusTrap();
          this.setInertToOtherElements();
          this.$refs.warningRef?.focus();
        });
      }
      if (this.alertType === 'check') {
        this.showCheckModal = true;
        this.propsMsg = this.msg;
        this.$nextTick(() => {
          this.setupFocusTrap();
          this.setInertToOtherElements();
          this.$refs.checkRef?.focus();
        });
      }
      if (this.alertType === 'confirm') {
        this.showConfirmModal = true;
        this.propsMsg = this.msg;
        this.propsConfirmCancelMessage = this.confirmCancelMessage
          ? this.confirmCancelMessage
          : '취소';
        this.propsConfirmOkMessage = this.confirmOkMessage
          ? this.confirmOkMessage
          : '확인';
        this.$nextTick(() => {
          this.setupFocusTrap();
          this.setInertToOtherElements();
          this.$refs.confirmRef?.focus();
        });
      }
    },

    setupFocusTrap() {
      let alertElement = null;

      if (this.showCheckModal) {
        alertElement = this.$refs.checkModal?.$el;
      } else if (this.showWarningModal) {
        alertElement = this.$refs.warningModal?.$el;
      } else if (this.showConfirmModal) {
        alertElement = this.$refs.confirmModal?.$el;
      }

      if (!alertElement) return;

      const focusableSelectors =
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

      this.focusableElements =
        alertElement.querySelectorAll(focusableSelectors);

      if (this.focusableElements.length > 0) {
        this.firstFocusableEl = this.focusableElements[0];
        this.lastFocusableEl =
          this.focusableElements[this.focusableElements.length - 1];

        alertElement.addEventListener('keydown', this.handleFocusTrap);
      }
    },

    handleFocusTrap(e) {
      const isTabPressed = e.key === 'Tab';

      if (!isTabPressed) return;

      if (e.shiftKey) {
        // Shift + Tab
        if (document.activeElement === this.firstFocusableEl) {
          this.lastFocusableEl?.focus();
          e.preventDefault();
        }
      } else {
        // Tab
        if (document.activeElement === this.lastFocusableEl) {
          this.firstFocusableEl?.focus();
          e.preventDefault();
        }
      }
    },

    setInertToOtherElements() {
      // section.alert가 아닌 모든 형제 요소들에 inert 속성 추가
      const alertElements = document.querySelectorAll('section.alert');
      if (alertElements.length === 0) return;

      const body = document.body;
      const children = Array.from(body.children);

      children.forEach(child => {
        // alert 또는 dim이 아닌 요소에만 inert 추가
        const isAlertOrDim =
          child.querySelector('section.alert') ||
          child.classList.contains('alert') ||
          child.classList.contains('dim') ||
          (child.tagName === 'SECTION' &&
            (child.classList.contains('alert') ||
              child.classList.contains('dim')));

        if (!isAlertOrDim) {
          child.setAttribute('inert', '');
          this.inertElements.push(child);
        }
      });
    },

    removeInertFromElements() {
      // inert 속성 제거
      this.inertElements.forEach(el => {
        el.removeAttribute('inert');
      });
      this.inertElements = [];

      // 이벤트 리스너 제거
      let alertElement = null;

      if (this.$refs.checkModal?.$el) {
        alertElement = this.$refs.checkModal.$el;
      } else if (this.$refs.warningModal?.$el) {
        alertElement = this.$refs.warningModal.$el;
      } else if (this.$refs.confirmModal?.$el) {
        alertElement = this.$refs.confirmModal.$el;
      }

      if (alertElement) {
        alertElement.removeEventListener('keydown', this.handleFocusTrap);
      }
    },

    closeModal() {
      this.removeInertFromElements();
      this.showCheckModal = false;
      this.showWarningModal = false;
      this.showConfirmModal = false;
      this.propsMsg = '';
      this.setAlertData();
      if (this.url) {
        this.$router.push(this.url);
      }
      this.$emit('focus');
    },

    confirmCheckModal() {
      this.removeInertFromElements();
      this.showConfirmModal = false;
      this.propsMsg = '';
      this.setAlertData(true);
      this.$emit('focus');
    },

    cancelModal() {
      this.removeInertFromElements();
      this.showConfirmModal = false;
      this.propsMsg = '';
      this.setAlertData();
      this.$emit('focus');
    },

    setAlertData(confirm) {
      this.$emit('setAlertData', confirm);
    },

    focusButton() {
      if (this.showCheckModal) {
        this.$refs.checkRef.focus();
      }
      if (this.showWarningModal) {
        this.$refs.warningRef.focus();
      }
      if (this.showConfirmModal) {
        this.$refs.confirmRef.focus();
      }
    },
  },

  beforeUnmount() {
    // 컴포넌트 제거 시 inert 정리
    this.removeInertFromElements();
  },
};
</script>
